import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Sincroniza filtros de listado + página con la URL para poder volver del detalle
 * con el mismo filtrado/paginado.
 *
 * @param extraKeysCsv claves extra separadas por coma (ej. "estado,reserva")
 */
export function useListUrlState(extraKeysCsv = '') {
  const [searchParams, setSearchParams] = useSearchParams();

  const keys = useMemo(() => {
    const extras = extraKeysCsv
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
    return Array.from(new Set(['q', 'cliente', 'sucursal', 'estado', 'page', ...extras]));
  }, [extraKeysCsv]);

  const filtersFromUrl = useMemo(() => {
    const filters: Record<string, any> = {};
    keys.forEach((key) => {
      if (key === 'page') return;
      const value = searchParams.get(key);
      if (value != null && value !== '') {
        if (value === 'true') filters[key] = true;
        else if (value === 'false') filters[key] = false;
        else if (/^\d+$/.test(value) && key !== 'q' && key !== 'estado') filters[key] = Number(value);
        else filters[key] = value;
      }
    });
    return filters;
  }, [searchParams, keys]);

  const pageFromUrl = useMemo(() => {
    const raw = Number(searchParams.get('page') || '1');
    return Number.isFinite(raw) && raw > 0 ? raw : 1;
  }, [searchParams]);

  const writeUrlState = useCallback(
    (filters: Record<string, any>, page = 1) => {
      const next = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value === '' || value == null || value === undefined) return;
        if (Array.isArray(value) && value.length === 0) return;
        next.set(key, String(value));
      });
      if (page > 1) next.set('page', String(page));
      // Evitar replace inútil si ya está igual (loop)
      if (next.toString() === searchParams.toString()) return;
      setSearchParams(next, { replace: true });
    },
    [setSearchParams, searchParams],
  );

  return { filtersFromUrl, pageFromUrl, writeUrlState, searchParams };
}

/** Vuelve al listado anterior (preserva query) o cae al path dado. */
export function navigateBackOr(navigate: (to: any) => void, fallbackPath: string) {
  if (typeof window !== 'undefined' && window.history.length > 1) {
    navigate(-1);
  } else {
    navigate(fallbackPath);
  }
}
